import type { PerformanceKpis } from '../../../../../schemas/performanceKpis.schemas';
import { SchemaNames } from '../../../../../constnats/schemaConstants';
import Assert from '../../../../../utils/asserts';
import { calcMistakeScore } from '../../utils/qualityUtils';
import { ConfigProps } from './constants';

/** Scores include per-cell weights, before applying the mistake-detection penalty. */
export function createWeightedVisualCheckScores() {
    return { kpis: 0, incomeStatement: 0, other: 0 };
}

type WeightedVisualCheckScores = ReturnType<typeof createWeightedVisualCheckScores>;
type VisualCheckTableCategory = keyof WeightedVisualCheckScores;
type QaDetectionTotals = { mistakePoints: number; cellsScope: number };

export function getVisualCheckTableCategory(tableType: string): VisualCheckTableCategory {
    if (tableType === SchemaNames.KPIs) {
        return 'kpis';
    }
    if (tableType === SchemaNames.IS) {
        return 'incomeStatement';
    }
    return 'other';
}

function adjustForMistakes(
    weightedScore: number,
    detectionRatePer100Cells: number | undefined,
    minimumRatePer100Cells: unknown,
): number {
    Assert.typeNumber(weightedScore);
    Assert.assertTrue(() => typeof detectionRatePer100Cells === 'number' || detectionRatePer100Cells === undefined);
    const detectionRate = detectionRatePer100Cells === undefined ? 0 : detectionRatePer100Cells;
    const minimumRate = Number(minimumRatePer100Cells);
    const divider = detectionRate >= minimumRate ? 1 : minimumRate / detectionRate;
    return weightedScore / divider;
}

/**
 * Receives each user's weighted scores split into KPIs, Income Statement, and Other.
 * Returns one visual-check total per user after applying the minimum QA-detection rates.
 */
export function getAdjustedVisualCheckTotalsByUser(
    weightedVisualCheckScoresByUser: Record<string, WeightedVisualCheckScores>,
    qaDetectionRecords: PerformanceKpis['qaEffectiveness'],
    includedTableTypes: string[],
    config: Record<string, unknown>,
): Record<string, number> {
    const includedTableTypeSet = new Set(includedTableTypes);
    const qaDetectionTotalsByUser: Record<string, Record<VisualCheckTableCategory, QaDetectionTotals>> = {};

    for (const userId of Object.keys(weightedVisualCheckScoresByUser)) {
        qaDetectionTotalsByUser[userId] = {
            kpis: { mistakePoints: 0, cellsScope: 0 },
            incomeStatement: { mistakePoints: 0, cellsScope: 0 },
            other: { mistakePoints: 0, cellsScope: 0 },
        };
    }

    // Sum detected points and reviewed cells for each user and table category.
    // Combine totals before calculating rates, including across different tables in Other.
    for (const qaRecord of qaDetectionRecords) {
        const userId = String(qaRecord.userId);
        if (weightedVisualCheckScoresByUser[userId] === undefined || !includedTableTypeSet.has(qaRecord.tableType)) {
            continue;
        }

        const tableCategory = getVisualCheckTableCategory(qaRecord.tableType);

        const categoryDetectionTotals = qaDetectionTotalsByUser[userId][tableCategory];
        categoryDetectionTotals.mistakePoints += qaRecord.detectedMistakes;
        categoryDetectionTotals.cellsScope += Number(qaRecord.cellsScope);
    }

    // All minimum detection rates are measured in mistake points per 100 QA-reviewed cells.
    const kpiMinimumTarget = config[ConfigProps.VC_KPI_MIN_MISTAKES];
    const incomeStatementMinimumTarget = config[ConfigProps.VC_IS_MIN_MISTAKES];
    const otherMinimumTarget = config[ConfigProps.VC_OTHER_MIN_MISTAKES];

    const adjustedVisualCheckTotalsByUser: Record<string, number> = {};
    for (const [userId, weightedVisualCheckScores] of Object.entries(weightedVisualCheckScoresByUser)) {
        const userQaDetectionTotals = qaDetectionTotalsByUser[userId];
        const adjustedKpiScore = adjustForMistakes(
            weightedVisualCheckScores.kpis,
            calcMistakeScore(userQaDetectionTotals.kpis),
            kpiMinimumTarget,
        );
        const adjustedIncomeStatementScore = adjustForMistakes(
            weightedVisualCheckScores.incomeStatement,
            calcMistakeScore(userQaDetectionTotals.incomeStatement),
            incomeStatementMinimumTarget,
        );
        const adjustedOtherScore = adjustForMistakes(
            weightedVisualCheckScores.other,
            calcMistakeScore(userQaDetectionTotals.other),
            otherMinimumTarget,
        );
        const adjustedVisualCheckTotal = adjustedKpiScore + adjustedIncomeStatementScore + adjustedOtherScore;

        adjustedVisualCheckTotalsByUser[userId] = adjustedVisualCheckTotal;
    }
    return adjustedVisualCheckTotalsByUser;
}
